import {
  assignPrototypeAPIs,
  assignTableAPIs,
  columnVisibilityFeature,
  type Cell,
  type CellData,
  type Column_Internal,
  type Header,
  type Row,
  type RowData,
  type Table_Internal,
  type TableFeature,
  type TableFeatures,
  type TableState,
} from '@tanstack/react-table';
import { isValidElement, type MouseEvent as ReactMouseEvent } from 'react';

import type {
  CollectedCellActions,
  DataTableCellActionsContent,
} from './CellActions.js';
import type { CollectedColumnActions } from './ColumnActions.js';
import type { UserActionsFeatureConstructors } from './user-actions-types.js';
import {
  filterActionsForChartColumn,
  getColumnActionsElements,
} from './user-actions-utils.js';
import { isProduction } from '../../../../core/utils/is-node-environment.js';
import { devLog } from '../../../../core/utils/logging.js';
import { column_getCanSort } from '../Sorting/Sorting.js';
import { SortingColumnActions } from '../Sorting/SortingColumnActions.js';
import type { TDataTableFeatures } from '../types/features.types.js';

export function table_getHeader<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  headerId: string,
  leafHeaders: Header<TFeatures, TData>[],
): Header<TFeatures, TData> | undefined {
  const header = leafHeaders.find((header) => header.id === headerId);

  if (!isProduction() && !header) {
    devLog(`[Table] Header with id '${headerId}' does not exist.`);
  }
  return header;
}

/**
 * Helper function to set the floating menu info in the table state.
 * @internal
 */
export function table_setFloatingMenuInfo<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  headerId: string | null,
  columnId: string,
  rowId: string | null,
  x: number,
  y: number,
): void {
  // Required cast to ensure presence of the DataTableFeatures.
  (
    table as unknown as Table_Internal<TDataTableFeatures>
  ).baseAtoms.floatingMenuInfo.set({
    type: rowId === null ? 'headerAction' : 'cellAction',
    headerId,
    rowId,
    columnId,
    x,
    y,
  });
  table_setOpenMenuRowId(table, rowId);
}

/**
 * Helper function to set the row id for the open menu.
 * @internal
 */
export function table_setOpenMenuRowId<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, rowId: string | null): void {
  // Required cast to ensure presence of the DataTableFeatures.
  (
    table as unknown as Table_Internal<TDataTableFeatures>
  ).baseAtoms.openMenuRowId.set(rowId);
}

/**
 * Helper function to get the row id for the open menu.
 * @internal
 */
export function table_getOpenMenuRowId<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): string | null {
  return table.atoms.openMenuRowId.get();
}

/**
 * Helper function to get the column actions for a given columnId.
 * @internal
 */
export function table_getUserActionsForColumn<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, columnId: string) {
  const column = table.getColumn(columnId);

  if (column?.getCanColumnActions()) {
    return column.getColumnActions();
  }

  return null;
}

/**
 * Helper function to get the cell actions for a given columnId and rowId.
 * @internal
 */
export function table_getUserActionsForCell<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, columnId: string, rowId: string) {
  // Requires a cast as the UnionToIntersection type used for feature distinction does not resolve properly.
  const row = table.getRow(rowId) as unknown as Row<
    { columnVisibilityFeature: typeof columnVisibilityFeature },
    TData
  >;
  const cell = row
    .getVisibleCells()
    .find((cell) => cell.column.id === columnId);

  if (cell?.column.getCanCellActions()) {
    return cell.getCellActions();
  }

  return null;
}

/**
 * Helper function to get the floating menu context menu handler for the user actions feature.
 * @internal
 */
export function table_getUserActionFloatingMenuContextMenuHandler<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
): (event: ReactMouseEvent<HTMLDivElement>) => void {
  return (event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      event.target instanceof HTMLElement === false &&
      event.target instanceof SVGElement === false
    ) {
      return;
    }

    const cellElement = event.target.closest<HTMLElement>('[role="cell"]');
    // the click was on a body cell
    if (cellElement) {
      const { columnid } = cellElement.dataset;
      const rowid = cellElement
        .closest('[role="row"]')
        ?.getAttribute('data-rowid');

      if (!columnid || !rowid) {
        return;
      }

      // In order to check if the column or cell could take actions we get the actual cell objects.
      const row = table.getRow(rowid);
      const cell = row.getAllCellsByColumnId()[columnid];

      if (cell?.column.getCanCellActions()) {
        table_setFloatingMenuInfo(
          table,
          null, // header is not needed for floating menu on body cell
          columnid,
          rowid,
          event.pageX,
          event.pageY,
        );
      }
      return;
    }

    const headerElement = event.target.closest<HTMLElement>(
      '[role="columnheader"]',
    );
    // the click happened on a header cell
    if (headerElement) {
      const { columnid, headerid } = headerElement.dataset;
      if (!columnid) {
        return;
      }

      // In order to check if the column or cell could take actions we get the actual column object.
      const column = table.getColumn(columnid);

      if (column?.getCanColumnActions()) {
        table_setFloatingMenuInfo(
          table,
          headerid ?? null,
          columnid,
          null,
          event.pageX,
          event.pageY,
        );
      }
    }
  };
}

/**
 * Helper function to get the column actions for a given column.
 * @internal
 */
export function column_getColumnActions<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  column: Column_Internal<TFeatures, TData>,
  columnId: string,
  columnActionsOption?: CollectedColumnActions<TData> | undefined,
  enableSortingOption?: boolean,
) {
  const columnActions = getColumnActionsElements(
    columnId,
    columnActionsOption ?? [],
    column.table.options.consumerColumnDefMap,
  );

  const isChartColumn =
    typeof column.columnDef.enableUserActions === 'object' &&
    column.columnDef.enableUserActions.type === 'chart';
  const filteredActions = isChartColumn
    ? filterActionsForChartColumn(columnActions)
    : columnActions;

  // Append sorting actions at the end when sorting is enabled for this column.
  if (enableSortingOption && column_getCanSort(column)) {
    filteredActions.push(
      <SortingColumnActions
        key="strato-table-sorting-column-actions"
        hasOtherColumnActions={filteredActions.length > 0}
      />,
    );
  }

  return filteredActions;
}

/**
 * Helper function to get if a column has any column actions.
 * @internal
 */
export function column_getCanColumnActions<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(column: Column_Internal<TFeatures, TData>) {
  if (column.columnDef.enableUserActions === false) {
    return false;
  }

  const columnActionsEnabled = column.table.options.enableColumnActions;
  const sortingEnabled =
    column.table.options.enableSorting && column_getCanSort(column);

  // the column cannot have any actions if neither column actions nor sorting are enabled for this column.
  if (!columnActionsEnabled && !sortingEnabled) {
    return false;
  }

  const definedColumnActions = column.getColumnActions();
  if (!definedColumnActions) {
    // runtime safety net
    return false;
  }

  return (definedColumnActions.length ?? 0) > 0;
}

/**
 * Helper function to get the cell actions for a given columnId.
 * @internal
 */
export function column_getCellActionsContent<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  column: Column_Internal<TFeatures, TData>,
  columnId: string,
  cellActionsOptions: CollectedCellActions<TData> = [],
): Array<DataTableCellActionsContent<TData>> {
  return cellActionsOptions.reduce(
    (actions, cellAction) => {
      if (
        cellAction.column === undefined ||
        // global action for all columns
        cellAction.column === columnId ||
        // column-specific action
        (typeof cellAction.column === 'function' && cellAction.column(columnId))
      ) {
        actions.push(cellAction.action);
      }
      return actions;
    },
    [] as Array<DataTableCellActionsContent<TData>>,
  );
}

/**
 * Helper function to get if the column has any cell actions.
 * @internal
 */
export function column_getCanCellActions<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(column: Column_Internal<TFeatures, TData>): boolean {
  if (column.columnDef.enableUserActions === false) {
    return false;
  }

  if (!column.table.options.enableCellActions) {
    return false;
  }

  const definedCellActions = column.getCellActionsContent();
  if (!definedCellActions) {
    // runtime safety net
    return false;
  }

  return (definedCellActions.length ?? 0) > 0;
}

/**
 * Helper function to get the cell actions for a given cell.
 * @internal
 */
export function cell_getCellActions<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData,
>(cell: Cell<TFeatures, TData, TValue>) {
  const cellActions = cell.column.getCellActionsContent().map((cellAction) => {
    // If the entry is a react element, we can directly return it
    if (isValidElement(cellAction)) {
      return cellAction;
    }
    // If it is a function, we will need to resolve the closure first
    if (typeof cellAction === 'function') {
      const columnDef = cell.table.options.consumerColumnDefMap?.get(
        cell.column.id,
      );
      if (columnDef !== undefined) {
        return cellAction({
          columnDef: columnDef,
          cellValue: cell.getValue(),
          row: cell.row.original,
          rowId: cell.row.id,
        });
      }
    }
    return null;
  });

  return cellActions;
}

/**
 * Constructs the user actions feature for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructUserActionsFeature<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(): TableFeature<UserActionsFeatureConstructors<TFeatures, TData>> {
  return {
    getInitialState: (initialState) => {
      return {
        ...initialState,
        floatingMenuInfo: structuredClone({
          columnId: null,
          rowId: null,
          x: 0,
          y: 0,
        }),
        openMenuRowId: null,
      } as unknown as TableState<TFeatures>;
      // This is safe here as the DataTableFeatures configuration, so it is a valid part of
      // TableState<TFeatures> at runtime — TypeScript just can't prove it
      // through the UnionToIntersection generic constraint.
    },
    assignColumnPrototype: (prototype, table) => {
      assignPrototypeAPIs('DataTableUserActionsFeature', prototype, table, {
        column_getColumnActions: {
          fn: (
            column,
            columnId: string,
            columnActionsOption?: CollectedColumnActions<TData> | undefined,
            enableSorting?: boolean,
          ) =>
            column_getColumnActions(
              column,
              columnId,
              columnActionsOption,
              enableSorting,
            ),
          memoDeps: (column) => [
            column.id,
            table.options.columnActions,
            table.options.enableSorting,
          ],
        },
        column_getCanColumnActions: {
          fn: (column) => column_getCanColumnActions(column),
        },
        column_getCellActionsContent: {
          fn: (
            column,
            columnId: string,
            cellActionsOptions: CollectedCellActions<TData>,
          ) =>
            column_getCellActionsContent(column, columnId, cellActionsOptions),
          memoDeps: (column) => [column.id, table.options.cellActions],
        },
        column_getCanCellActions: {
          fn: (column) => column_getCanCellActions(column),
        },
      });
    },
    assignCellPrototype: (prototype, table) => {
      assignPrototypeAPIs('DataTableUserActionsFeature', prototype, table, {
        cell_getCellActions: {
          fn: (cell) => cell_getCellActions(cell),
          memoDeps: (cell) => [cell.column.id, table.options.cellActions],
        },
      });
    },
    constructTableAPIs: (table) => {
      assignTableAPIs('DataTableUserActionsFeature', table, {
        table_getHeader: {
          fn: (headerId: string, leafHeaders: Header<TFeatures, TData>[]) =>
            table_getHeader<TFeatures, TData>(
              table as unknown as Table_Internal<TFeatures, TData>,
              headerId,
              leafHeaders,
            ),
          memoDeps: (headerId) => [headerId, table.getLeafHeaders()],
        },
        table_setFloatingMenuInfo: {
          fn: (
            headerId: string | null,
            columnId: string,
            rowId: string | null,
            x: number,
            y: number,
          ) =>
            table_setFloatingMenuInfo(table, headerId, columnId, rowId, x, y),
        },
        table_setOpenMenuRowId: {
          fn: (rowId: string | null) => table_setOpenMenuRowId(table, rowId),
        },
        table_getOpenMenuRowId: {
          fn: () => table_getOpenMenuRowId(table),
        },
        table_getUserActionsForColumn: {
          fn: (columnId: string) =>
            table_getUserActionsForColumn(table, columnId),
        },
        table_getUserActionsForCell: {
          fn: (columnId: string, rowId: string) =>
            table_getUserActionsForCell(table, columnId, rowId),
        },
        table_getUserActionFloatingMenuContextMenuHandler: {
          fn: () => table_getUserActionFloatingMenuContextMenuHandler(table),
        },
      });
    },
  };
}

/**
 * Feature to handle user actions such as column and cell actions, including floating menu context.
 * @internal
 */
export const DataTableUserActionsFeature = constructUserActionsFeature();
